/**
 * AffiliateForgotPassword — first step of the portal's password reset.
 *
 * The affiliate identity is separate from the member session, so this calls the
 * affiliate reset endpoints rather than `authService`: the member routes do not
 * know an affiliate account.
 *
 * The API answers with a token for any address, registered or not, so this
 * screen cannot say whether an account exists — an unknown address simply
 * never receives a code, which the OTP step reports.
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import affiliateService from '@wlb/shared/api/affiliate/affiliateService';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { validateEmail } from '@wlb/shared/utils/validation';
import { FormError } from '../components/ui/FormError';
import { AffiliatePortalLayout } from '../features/affiliate/AffiliatePortalLayout';
import type { AffiliatePasswordResetState } from '../features/affiliate/affiliateFlowState';
import { AFFILIATE_ROUTES } from '../features/affiliate/affiliateRoutes';
import '../components/layout/authFlow.css';
import '../features/affiliate/affiliatePortal.css';

export function AffiliateForgotPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError('');

    const result = validateEmail(email);
    if (!result.valid) {
      setEmailError(t(result.message));
      return;
    }
    setEmailError('');

    setSubmitting(true);
    try {
      const trimmed = email.trim();
      const { passwordResetToken } = await affiliateService.forgotPassword(trimmed);
      const next: AffiliatePasswordResetState = { email: trimmed, passwordResetToken };
      navigate(AFFILIATE_ROUTES.VERIFICATION_CODE, { state: next });
    } catch (error) {
      setFormError(
        getUserFacingErrorMessage(error, t('affiliate.forgotPassword.failed')),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AffiliatePortalLayout centered>
      <div className="auth-flow__card">
        <h1 className="auth-flow__heading">{t('affiliate.forgotPassword.heading')}</h1>
        <p className="auth-flow__subheading">{t('affiliate.forgotPassword.subheading')}</p>
        <form className="auth-flow__form" onSubmit={handleSubmit} noValidate>
          <FormError
            variant="banner"
            message={formError}
            id="affiliate-forgot-password-error"
          />
          <label className="auth-flow__field">
            <span>{t('affiliate.signIn.emailLabel')}</span>
            <input
              className={`auth-flow__input${emailError ? ' auth-flow__input--error' : ''}`}
              type="email"
              value={email}
              onChange={event => {
                setEmail(event.target.value);
                if (emailError) setEmailError('');
              }}
              placeholder={t('affiliate.signIn.emailPlaceholder')}
              autoComplete="email"
              aria-invalid={Boolean(emailError)}
              aria-describedby={
                emailError ? 'affiliate-forgot-password-email-error' : undefined
              }
              autoFocus
            />
            <FormError message={emailError} id="affiliate-forgot-password-email-error" />
          </label>
          <button className="auth-flow__primary" type="submit" disabled={submitting}>
            {submitting ? t('common.loading') : t('affiliate.forgotPassword.sendCode')}
          </button>
        </form>
        <p className="auth-flow__footer">
          <Link to={AFFILIATE_ROUTES.SIGN_IN}>
            {t('auth.passwordResetSuccess.backToSignIn')}
          </Link>
        </p>
      </div>
    </AffiliatePortalLayout>
  );
}
